// UserDetailsModal.tsx - Rewritten to match ViewDetailsModal design pattern with multi-role display
import React, { useState } from 'react';
import './FocalPointsManagement.css';
import './UserDetailsModal.css';
import '../companyManagement/ViewDetailsModal.css';
import { User } from './types';

// ─── Role parsing (inline to avoid import issues with .css/.tsx confusion) ───
function parseUserRoles(roleStr: string | null | undefined): string[] {
  if (!roleStr) return [];
  return roleStr.split(',').map(r => r.trim()).filter(r => r);
}

const ROLE_LABEL_MAP: Record<string, string> = {
  user: 'Standard User',
  focal_point: 'Focal Point',
  reviewer: 'Company Reviewer',
  approver: 'Company Approver',
  gate_reviewer: 'Gate / Asset Reviewer',
  gate_approver: 'Gate / Asset Approver',
  gate_user: 'Gate User',
  gate_supervisor: 'Gate Supervisor',
  security_officer: 'Security Officer',
  // Legacy single-role values (pre multi-role update)
  'focal point': 'Focal Point',
  'Reviewer': 'Company Reviewer',
  'Approver': 'Company Approver',
  'Gate User': 'Gate User',
  'Gate Supervisor': 'Gate Supervisor',
  'Security Officer': 'Security Officer',
};

interface UserDetailsModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (user: User) => void;
  onDelete?: (user: User) => void;
  isExternal?: boolean;
  adStatus?: 'sent' | 'accepted' | 'pending' | 'expired' | 'not_sent';
  onSendInvitation?: (user: User) => void;
  onResendInvitation?: (user: User) => void;
  onSyncInvitationStatus?: (user: User) => void;
  isSyncing?: boolean;
}

export const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  user,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  isExternal,
  adStatus,
  onSendInvitation,
  onResendInvitation,
  onSyncInvitationStatus,
  isSyncing
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleAction = (action: ((u: User) => void) | undefined) => {
    if (action) {
      onClose();
      action(user);
    }
  };

  // Parse roles from DB value
  const roles = parseUserRoles(user.role);
  const userTypeDisplay = user.userType === 'company' ? 'Company' : 'Gate';

  // ─── Role pill style ───
  const rolePillStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 14px',
    borderRadius: '14px',
    fontSize: '12.5px',
    fontWeight: 600,
    fontFamily: "'Segoe UI', sans-serif",
    background: '#FFF5F2',
    color: '#c0452a',
    border: '1px solid rgba(243, 105, 74, 0.25)',
    lineHeight: '1.4',
  };

  return (
    <div className="modal-overlay view-details-overlay" onClick={onClose}>
      <div className="view-details-modal" onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className="view-details-header">
          <div className="view-details-header-left">
            <h2 className="view-details-title">{user.userName}</h2>
            {user.isBlacklisted ? (
              <span className="vdm-status-badge blacklisted">Blacklisted</span>
            ) : (
              <span className="vdm-status-badge active">Active</span>
            )}
          </div>
          <button 
            type="button" 
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="view-details-body">
          <div className="view-details-section">
            <h3 className="section-title">User Information</h3>
          </div>

          <div className="view-details-grid">
            <div className="view-details-left">
              <div className="detail-field">
                <label className="detail-label">User Name</label>
                <div className="detail-value">{user.userName}</div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Email</label>
                <div className="detail-value email">
                  <a href={`mailto:${user.userEmail}`} onClick={(e) => e.stopPropagation()}>
                    {user.userEmail}
                  </a>
                </div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Phone</label>
                <div className="detail-value">{user.userPhoneNo || 'N/A'}</div>
              </div>

              <div className="detail-field">
                <label className="detail-label">Nationality</label>
                <div className="detail-value">{user.nationality || 'N/A'}</div>
              </div>
            </div>

            <div className="view-details-right">
              <div className="detail-field">
                <label className="detail-label">User Type</label>
                <div className="detail-value">{userTypeDisplay}</div>
              </div>

              {user.companyName && (
                <div className="detail-field">
                  <label className="detail-label">Company</label>
                  <div className="detail-value company-name">{user.companyName}</div>
                </div>
              )}

              {user.functionName && (
                <div className="detail-field">
                  <label className="detail-label">Division</label>
                  <div className="detail-value">{user.functionName}</div>
                </div>
              )}

              <div className="detail-field">
                <label className="detail-label">Created At</label>
                <div className="detail-value">{formatDate(user.createdAt)}</div>
              </div>

              {user.updatedAt && (
                <div className="detail-field">
                  <label className="detail-label">Last Updated</label>
                  <div className="detail-value">{formatDate(user.updatedAt)}</div>
                </div>
              )}
            </div>
          </div>

          {/* ── Roles Section — Full Width ── */}
          <div className="view-details-section" style={{ marginTop: '4px' }}>
            <div className="detail-field full-width" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px', borderBottom: 'none' }}>
              <label className="detail-label">Assigned Roles</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {roles.length > 0 ? (
                  roles.map((r) => (
                    <span key={r} style={rolePillStyle}>
                      {ROLE_LABEL_MAP[r] || r}
                    </span>
                  ))
                ) : (
                  <span style={{ color: '#9ca3af', fontSize: '14px', fontStyle: 'italic' }}>
                    No roles assigned
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="view-details-footer">
          <div className="vdm-actions-left">
            {onDelete && !showDeleteConfirm && (
              <button 
                type="button" 
                className="vdm-action-btn delete"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Delete
              </button>
            )}
            {showDeleteConfirm && (
              <div className="vdm-delete-confirm">
                <span className="vdm-delete-confirm-text">Delete this user?</span>
                <button 
                  type="button" 
                  className="vdm-action-btn delete-confirm"
                  onClick={() => handleAction(onDelete)}
                >
                  Yes, Delete
                </button>
                <button 
                  type="button" 
                  className="vdm-action-btn cancel-delete"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div className="vdm-actions-right">
            {isExternal && adStatus === 'not_sent' && onSendInvitation && (
              <button 
                type="button" 
                className="vdm-action-btn secondary"
                onClick={() => handleAction(onSendInvitation)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                </svg>
                Send AD Invite
              </button>
            )}
            {isExternal && (adStatus === 'sent' || adStatus === 'pending') && onSyncInvitationStatus && (
              <button 
                type="button" 
                className="vdm-action-btn secondary"
                onClick={() => handleAction(onSyncInvitationStatus)}
                disabled={isSyncing}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
                {isSyncing ? 'Refreshing...' : 'Refresh Status'}
              </button>
            )}
            {isExternal && (adStatus === 'sent' || adStatus === 'pending' || adStatus === 'expired') && onResendInvitation && (
              <button 
                type="button" 
                className="vdm-action-btn secondary"
                onClick={() => handleAction(onResendInvitation)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                </svg>
                Resend Invite
              </button>
            )}
            {onEdit && (
              <button 
                type="button" 
                className="vdm-action-btn primary"
                onClick={() => handleAction(onEdit)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};